import { useCallback } from 'react';
import {
  Environment, VMType, OSType, Disk,
  VMPricingRow, DiskPricingRow, AddonPricingRow,
  DatabasePricingRow, DatabaseEditionPricingRow, AddonOptionPricingRow,
  PowerVMPricingRow, PowerAddonPricingRow, PowerAddonOptionPricingRow, PowerDiskPricingRow,
  BackupPricingRow, DatabaseBackupPricingRow, DatabaseExtraStoragePricingRow,
  DRTestingPricingRow,
} from '../../types';
import { NormalizedDefaultAddon, NormalizedPowerDefaultAddon, VMRow, PowerVMRow } from './types';

interface UsePriceCalculationParams {
  vmTypes: VMType[];
  osTypes: OSType[];
  environments: Environment[];
  filteredVMTypes: VMType[];
  disks: Disk[];
  vmPricing: Record<string, VMPricingRow>;
  diskPricing: Record<string, DiskPricingRow>;
  addonPricing: Record<string, AddonPricingRow>;
  databasePricing: Record<string, DatabasePricingRow>;
  databaseVMPricing: Record<string, DatabaseEditionPricingRow>;
  addonOptionPricing: Record<string, AddonOptionPricingRow>;
  defaultAddons: NormalizedDefaultAddon[];
  selectedRegion: number | string;
  powerVMPricing: Record<string, PowerVMPricingRow>;
  powerAddonPricing: Record<string, PowerAddonPricingRow>;
  powerAddonOptionPricing: Record<string, PowerAddonOptionPricingRow>;
  powerDefaultAddons: NormalizedPowerDefaultAddon[];
  powerDiskPricing: Record<string, PowerDiskPricingRow>;
  backupPricing: Record<string, BackupPricingRow>;
  databaseBackupPricing: Record<string, DatabaseBackupPricingRow>;
  databaseExtraStoragePricing: Record<string, DatabaseExtraStoragePricingRow>;
  drTestingPricing: Record<string, DRTestingPricingRow>;
}

export const usePriceCalculation = ({
  vmTypes, osTypes, environments, filteredVMTypes, disks,
  vmPricing, diskPricing, addonPricing, databasePricing, databaseVMPricing, addonOptionPricing,
  defaultAddons, selectedRegion,
  powerVMPricing, powerAddonPricing, powerAddonOptionPricing, powerDefaultAddons, powerDiskPricing,
  backupPricing, databaseBackupPricing, databaseExtraStoragePricing,
  drTestingPricing,
}: UsePriceCalculationParams) => {

  const getOSCategoryById = useCallback((osTypeId: number | string) =>
    osTypes.find((o) => o.OSTypeId === parseInt(String(osTypeId), 10))?.OSCategory ?? null,
  [osTypes]);

  const getVMPrice = useCallback((vmTypeId: number | undefined, osTypeId: number | string, regionId: number | string) => {
    const cat = getOSCategoryById(osTypeId); if (!cat) return 0;
    const p = vmPricing[`${vmTypeId}-${cat}-${regionId}`];
    return p?.IsAvailable ? p.MonthlyCost : 0;
  }, [getOSCategoryById, vmPricing]);

  const getDiskPrice = useCallback((diskId: number | null, regionId: number | string) => {
    if (!diskId) return 0;
    const p = diskPricing[`${diskId}-${regionId}`];
    return p?.IsAvailable ? p.MonthlyCost : 0;
  }, [diskPricing]);

  const getDiskSizeGB = useCallback((diskId: number | null): number => {
    if (!diskId) return 0;
    const disk = disks.find(d => d.DiskId === diskId);
    const raw = disk?.DiskSize;
    if (raw == null) return 0;
    return typeof raw === 'number' ? raw : (parseInt(String(raw), 10) || 0);
  }, [disks]);

  const getBackupCostPerGB = useCallback((regionId: number | string) => {
    const p = backupPricing[`${regionId}`];
    return p?.IsAvailable ? p.CostPerGB : 0;
  }, [backupPricing]);

  const getDRTestingCost = useCallback((frequency: number, regionId: number | string) => {
    if (!frequency) return 0;
    const p = drTestingPricing[`${regionId}`];
    return p?.IsAvailable ? p.CostPerTest * frequency : 0;
  }, [drTestingPricing]);

  const getDatabaseBackupCostPerGB = useCallback((regionId: number | string) => {
    const p = databaseBackupPricing[`${regionId}`];
    return p?.IsAvailable ? p.CostPerGB : 0;
  }, [databaseBackupPricing]);

  const getDatabaseExtraStorageCostPerGB = useCallback((regionId: number | string) => {
    const p = databaseExtraStoragePricing[`${regionId}`];
    return p?.IsAvailable ? p.CostPerGB : 0;
  }, [databaseExtraStoragePricing]);

  const getDatabasePrice = useCallback((configId: number | string, regionId: number | string) => {
    const p = databasePricing[`${configId}-${regionId}`];
    return p?.IsAvailable ? p.MonthlyCost : 0;
  }, [databasePricing]);

  const getDatabaseTotalPrice = useCallback((configId: number | string, regionId: number | string, dailyBackups: number, extraStorageGB: number) => {
    const basePrice = getDatabasePrice(configId, regionId);
    if (!basePrice) return 0;
    const backupCostPerGB = getDatabaseBackupCostPerGB(regionId);
    const extraStorageCostPerGB = getDatabaseExtraStorageCostPerGB(regionId);
    const backupCost = extraStorageGB * backupCostPerGB * Math.max(0, dailyBackups - 1);
    const extraStorageCost = extraStorageGB * extraStorageCostPerGB;
    return basePrice + backupCost + extraStorageCost;
  }, [getDatabasePrice, getDatabaseBackupCostPerGB, getDatabaseExtraStorageCostPerGB]);

  const getDatabaseVMPrice = useCallback((vmTypeId: number | undefined, editionId: number | string, regionId: number | string, dbDisks: (number | null)[], environmentId: number) => {
    const p = databaseVMPricing[`${vmTypeId}-${editionId}-${regionId}`];
    const environment = environments.find(e => e.EnvironmentId === (environmentId ?? 1));
    const discountMultiplier = environment?.DatabaseVMDiscountMultiplier ?? 1.0;
    const diskDiscountMultiplier = environment?.DiskDiscountMultiplier ?? 1.0;

    const baseDBCost = p?.IsAvailable ? p.MonthlyCost : 0;
    const discountedDBCost = baseDBCost * discountMultiplier;

    let disksSum = 0;
    (dbDisks ?? []).forEach((diskId) => { if (diskId) disksSum += getDiskPrice(diskId, regionId); });
    disksSum *= diskDiscountMultiplier;

    const envDefaultAddons = defaultAddons.filter(da =>
      da.EnvironmentId === (environmentId ?? 1) && da.ApplyToDatabaseVMs
    );
    const addonsSum = envDefaultAddons.reduce((sum, da) =>
      sum + (addonPricing[`${da.AddonId}-${regionId}`]?.MonthlyCost ?? 0), 0
    );

    return discountedDBCost + disksSum + addonsSum;
  }, [databaseVMPricing, environments, getDiskPrice, defaultAddons, addonPricing]);

  const getAddonCostByIds = useCallback((addonId: number, optionId: number | null) => {
    if (optionId) {
      const p = addonOptionPricing[`${optionId}-${selectedRegion}`];
      return p?.MonthlyCost ?? 0;
    }
    const p = addonPricing[`${addonId}-${selectedRegion}`];
    return p?.MonthlyCost ?? 0;
  }, [addonOptionPricing, addonPricing, selectedRegion]);

  const getAvailableRAM = useCallback((cpu: number) =>
    [...new Set(filteredVMTypes.filter((v) => v.CPU === cpu).map((v) => v.RAM))].sort((a, b) => a - b),
  [filteredVMTypes]);

  const getAvailableRAMForDatabase = useCallback((cpu: number) =>
    [...new Set(filteredVMTypes.filter((v) => v.CPU === cpu).map((v) => v.RAM))].sort((a, b) => a - b),
  [filteredVMTypes]);

  const getVMTypeId = useCallback((cpu: number, ram: number) =>
    vmTypes.find((v) => v.CPU === cpu && v.RAM === ram)?.VMTypeId,
  [vmTypes]);

  const calculateVMCost = useCallback((vm: VMRow) => {
    const vmTypeId = getVMTypeId(vm.cpu, vm.ram);
    if (!vmTypeId) return 0;

    const environment = environments.find(e => e.EnvironmentId === (vm.environmentId ?? 1));
    const discountMultiplier = environment?.VMDiscountMultiplier ?? 1.0;
    const diskDiscountMultiplier = environment?.DiskDiscountMultiplier ?? 1.0;

    const baseVMCost = getVMPrice(vmTypeId, vm.osTypeId, selectedRegion);
    const discountedVMCost = baseVMCost * discountMultiplier;

    const disksSum = (vm.disks ?? []).reduce((s: number, d) => s + (d ? getDiskPrice(d, selectedRegion) : 0), 0) * diskDiscountMultiplier;

    const envDefaultAddons = defaultAddons.filter(da =>
      da.EnvironmentId === (vm.environmentId ?? 1) && da.ApplyToVMs
    );
    const addonsSum = envDefaultAddons.reduce((sum, da) =>
      sum + (addonPricing[`${da.AddonId}-${selectedRegion}`]?.MonthlyCost ?? 0), 0
    );

    // Backup cost: sum of disk sizes × backupCostPerGB × (paid backups, first is free)
    const totalDiskGB = (vm.disks ?? []).reduce((s: number, d) => s + getDiskSizeGB(d), 0);
    const backupCostPerGB = getBackupCostPerGB(selectedRegion);
    const backupCost = totalDiskGB * backupCostPerGB * Math.max(0, (vm.dailyBackups ?? 0) - 1);

    // DR testing cost: flat rate per frequency
    const drTestingCost = getDRTestingCost(vm.drTestingFrequency ?? 0, selectedRegion);

    return (discountedVMCost + disksSum + addonsSum + backupCost + drTestingCost) * vm.quantity;
  }, [getVMTypeId, environments, getVMPrice, selectedRegion, getDiskPrice, defaultAddons, addonPricing, getDiskSizeGB, getBackupCostPerGB, getDRTestingCost]);

  const getPowerDiskPrice = useCallback((diskId: number | null, regionId: number | string) => {
    if (!diskId) return 0;
    const p = powerDiskPricing[`${diskId}-${regionId}`];
    return p?.IsAvailable ? p.MonthlyCost : 0;
  }, [powerDiskPricing]);

  const calculatePowerVMCost = useCallback((row: PowerVMRow) => {
    const key = `${row.powerTypeId}-${selectedRegion}`;
    const pricing = powerVMPricing[key];
    if (!pricing?.IsAvailable) return 0;

    const environment = environments.find(e => e.EnvironmentId === (row.environmentId ?? 1));
    const discountMultiplier = environment?.VMDiscountMultiplier ?? 1.0;
    const diskDiscountMultiplier = environment?.DiskDiscountMultiplier ?? 1.0;

    const cpuCost = (row.cpu / 0.25) * pricing.CostPerQuarterCore;
    const ramCost = row.ram * pricing.CostPerGBRam;
    const baseCost = (cpuCost + ramCost) * discountMultiplier;

    // Use Power-specific disk pricing with optional per-environment storage discount
    const disksSum = (row.disks ?? []).reduce((s: number, d) => s + (d ? getPowerDiskPrice(d, selectedRegion) : 0), 0) * diskDiscountMultiplier;

    const envPowerDefaultAddons = powerDefaultAddons.filter(da =>
      da.EnvironmentId === (row.environmentId ?? 1)
    );
    const addonsSum = envPowerDefaultAddons.reduce((sum, da) =>
      sum + (powerAddonPricing[`${da.PowerAddonId}-${selectedRegion}`]?.MonthlyCost ?? 0), 0
    );

    return (baseCost + disksSum + addonsSum) * row.quantity;
  }, [selectedRegion, powerVMPricing, environments, getPowerDiskPrice, powerDefaultAddons, powerAddonPricing]);

  const getPowerAddonCost = useCallback((powerAddonId: number) => {
    const p = powerAddonPricing[`${powerAddonId}-${selectedRegion}`];
    return p?.MonthlyCost ?? 0;
  }, [powerAddonPricing, selectedRegion]);

  const getPowerAddonCostByIds = useCallback((powerAddonId: number, optionId: number | null) => {
    if (optionId) {
      const p = powerAddonOptionPricing[`${optionId}-${selectedRegion}`];
      return p?.MonthlyCost ?? 0;
    }
    const p = powerAddonPricing[`${powerAddonId}-${selectedRegion}`];
    return p?.MonthlyCost ?? 0;
  }, [powerAddonOptionPricing, powerAddonPricing, selectedRegion]);

  return {
    getOSCategoryById, getVMPrice, getDiskPrice, getDiskSizeGB,
    getDatabasePrice, getDatabaseTotalPrice,
    getDatabaseVMPrice, getAddonCostByIds, getAvailableRAM, getAvailableRAMForDatabase,
    getVMTypeId, calculateVMCost,
    calculatePowerVMCost, getPowerAddonCost, getPowerAddonCostByIds, getPowerDiskPrice,
    getBackupCostPerGB, getDatabaseBackupCostPerGB, getDatabaseExtraStorageCostPerGB,
    getDRTestingCost,
  };
};
